import { 
  CompleteKundali, 
  PlanetName, 
  ZodiacSign, 
  PlanetPosition, 
  Language 
} from '../types/kundali';
import { 
  PlanetaryMatrixEntry, 
  BhaveshMatrixEntry, 
  CompletePlanetaryBhaveshMatrix, 
  FunctionalRoleInfo 
} from '../types/planetaryMatrix';
import { 
  ZODIAC_LORDS, 
  ZODIAC_SIGNS,
  getSavBySign,
  getBavBySign
} from './vedicEngine';
import { 
  getPanchadhaMaitriCompound, 
  getCompoundRelationshipHindi,
  NATURAL_FRIENDSHIPS,
  PLANET_OWN_SIGNS
} from './planetaryDignityEngine';
import { calculateShadbala, getBirthUtcDate } from './vedicEngine';
import { calculatePrastaraTables } from './ashtakvargaAdvancedEngine';
import { getAspectsOnHouse } from './astrologyRules';

export const getExactDignity = (planet: string, signIndex: number, deg: number): string => {
  // Exaltation points
  if (planet === 'Sun' && signIndex === 0 && deg <= 10) return 'Deep Exaltation (Paramochha)';
  if (planet === 'Sun' && signIndex === 0) return 'Exalted';
  if (planet === 'Moon' && signIndex === 1 && deg <= 3) return 'Deep Exaltation (Paramochha)';
  if (planet === 'Moon' && signIndex === 1) return 'Exalted';
  if (planet === 'Mars' && signIndex === 9 && deg <= 28) return 'Exalted';
  if (planet === 'Mercury' && signIndex === 5 && deg <= 15) return 'Exalted (Paramochha)';
  if (planet === 'Jupiter' && signIndex === 3 && deg <= 5) return 'Exalted';
  if (planet === 'Venus' && signIndex === 11 && deg <= 27) return 'Exalted';
  if (planet === 'Saturn' && signIndex === 6 && deg <= 20) return 'Exalted';
  if (planet === 'Rahu' && signIndex === 1) return 'Exalted';
  if (planet === 'Ketu' && signIndex === 7) return 'Exalted';

  // Debilitation points
  if (planet === 'Sun' && signIndex === 6) return 'Debilitated';
  if (planet === 'Moon' && signIndex === 7) return 'Debilitated';
  if (planet === 'Mars' && signIndex === 3) return 'Debilitated';
  if (planet === 'Mercury' && signIndex === 11) return 'Debilitated';
  if (planet === 'Jupiter' && signIndex === 9) return 'Debilitated';
  if (planet === 'Venus' && signIndex === 5) return 'Debilitated';
  if (planet === 'Saturn' && signIndex === 0) return 'Debilitated';
  if (planet === 'Rahu' && signIndex === 7) return 'Debilitated';
  if (planet === 'Ketu' && signIndex === 1) return 'Debilitated';

  // Moolatrikona
  if (planet === 'Sun' && signIndex === 4 && deg <= 20) return 'Moolatrikona';
  if (planet === 'Moon' && signIndex === 1 && deg > 3 && deg <= 20) return 'Moolatrikona';
  if (planet === 'Mars' && signIndex === 0 && deg <= 12) return 'Moolatrikona';
  if (planet === 'Mercury' && signIndex === 5 && deg > 15 && deg <= 20) return 'Moolatrikona';
  if (planet === 'Jupiter' && signIndex === 8 && deg <= 10) return 'Moolatrikona';
  if (planet === 'Venus' && signIndex === 6 && deg <= 15) return 'Moolatrikona';
  if (planet === 'Saturn' && signIndex === 10 && deg <= 20) return 'Moolatrikona';

  const ownSigns: Record<string, number[]> = {
    Sun: [4], Moon: [3], Mars: [0, 7], Mercury: [2, 5], Jupiter: [8, 11], Venus: [1, 6], Saturn: [9, 10]
  };
  
  if (ownSigns[planet]?.includes(signIndex)) return 'Own House';

  const ZODIAC_LORDS_ARR = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
  const signLord = ZODIAC_LORDS_ARR[signIndex];

  if (planet === 'Rahu' || planet === 'Ketu') return 'Neutral';

  const friendships = NATURAL_FRIENDSHIPS[planet as PlanetName];
  if (!friendships) return 'Neutral';
  if (friendships.friends.includes(signLord as PlanetName)) return 'Friend';
  if (friendships.enemies.includes(signLord as PlanetName)) return 'Enemy';
  return 'Neutral';
};

export const isPushkarNavamsha = (signIndex: number, navamshaSignIndex: number): boolean => {
  if ([0, 4, 8].includes(signIndex) && [6, 8].includes(navamshaSignIndex)) return true;
  if ([1, 5, 9].includes(signIndex) && [11, 1].includes(navamshaSignIndex)) return true;
  if ([2, 6, 10].includes(signIndex) && [11, 1].includes(navamshaSignIndex)) return true;
  if ([3, 7, 11].includes(signIndex) && [3, 5].includes(navamshaSignIndex)) return true;
  return false;
};

export const isPushkarBhaga = (signIndex: number, degree: number): boolean => {
  const pushkarDegrees: Record<number, number> = {
    0: 21, 1: 14, 2: 18, 3: 8, 4: 19, 5: 9, 6: 24, 7: 11, 8: 23, 9: 14, 10: 19, 11: 9
  };
  const targetDeg = pushkarDegrees[signIndex];
  return targetDeg !== undefined && Math.abs(degree - targetDeg) <= 1.0;
};

const HOUSE_THEMES_DATA: Record<number, { titleEn: string; titleHi: string; sanskritName: string; meaningEn: string; meaningHi: string; vargas: string[] }> = {
  1: {
    titleEn: '1st House (Tanu)',
    titleHi: 'प्रथम भाव (तनु)',
    sanskritName: 'Tanu Bhava',
    meaningEn: 'Self, Physical Constitution, Vitality, Temperament, Head & General Life Path',
    meaningHi: 'व्यक्तित्व, शारीरिक गठन, जीवन ऊर्जा, स्वभाव, मस्तक व संपूर्ण जीवन दिशा',
    vargas: ['D1', 'D9']
  },
  2: {
    titleEn: '2nd House (Dhana)',
    titleHi: 'द्वितीय भाव (धन)',
    sanskritName: 'Dhana Bhava',
    meaningEn: 'Accumulated Wealth, Speech, Immediate Family, Liquid Assets, Diet & Facial Features',
    meaningHi: 'संचित कोष, वाणी, परिवार, चल संपत्ति, खान-पान व मुख सौंदर्य',
    vargas: ['D1', 'D2', 'D9']
  },
  3: {
    titleEn: '3rd House (Sahaja)',
    titleHi: 'तृतीय भाव (सहज)',
    sanskritName: 'Sahaja Bhava',
    meaningEn: 'Courage, Enterprise, Younger Siblings, Communication Skills, Media & Short Travels',
    meaningHi: 'पराक्रम, उद्यमशीलता, छोटे भाई-बहन, संवाद कौशल, लेखन व लघु यात्राएं',
    vargas: ['D1', 'D3', 'D9']
  },
  4: {
    titleEn: '4th House (Sukha)',
    titleHi: 'चतुर्थ भाव (सुख)',
    sanskritName: 'Sukha / Matru Bhava',
    meaningEn: 'Mother, Emotional Contentment, Fixed Properties, Land, Vehicles, Domestic Haven & Formal Education',
    meaningHi: 'माता, मन का सुख, अचल संपत्ति, भूमि, वाहन, गृह शांति व प्राथमिक शिक्षा',
    vargas: ['D1', 'D4', 'D16']
  },
  5: {
    titleEn: '5th House (Putra)',
    titleHi: 'पंचम भाव (पुत्र)',
    sanskritName: 'Putra & Buddhi Bhava',
    meaningEn: 'Creative Intellect, Progeny, Purva Punya (Past Karma), Mantras, Intuition & Speculative Fortune',
    meaningHi: 'सृजनात्मक मेधा, संतान, पूर्व जन्म के पुण्य, मंत्र सिद्धि, अंतःप्रेरणा व आकस्मिक लाभ',
    vargas: ['D1', 'D7', 'D24']
  },
  6: {
    titleEn: '6th House (Shatru)',
    titleHi: 'षष्ठ भाव (शत्रु/अरि)',
    sanskritName: 'Ari / Rina Bhava',
    meaningEn: 'Daily Professional Service, Overcoming Competitors, Debts, Acute Health, Litigation & Tenacity',
    meaningHi: 'दैनिक सेवा/नौकरी, रोग प्रतिरोधक क्षमता, ऋण मुक्ति, प्रतियोगिता विजय व परिश्रम',
    vargas: ['D1', 'D10', 'D30']
  },
  7: {
    titleEn: '7th House (Kalatra)',
    titleHi: 'सप्तम भाव (कलत्र/जाया)',
    sanskritName: 'Kalatra / Jaya Bhava',
    meaningEn: 'Spouse, Long-term Marital Union, Business Partnerships, Public Interactions & Foreign Alliances',
    meaningHi: 'जीवनसाथी, दांपत्य जीवन, व्यापारिक साझेदारी, जनसंपर्क व विदेशी अनुबंध',
    vargas: ['D1', 'D9']
  },
  8: {
    titleEn: '8th House (Randhra)',
    titleHi: 'अष्टम भाव (रंध्र/आयु)',
    sanskritName: 'Randhra / Ayur Bhava',
    meaningEn: 'Longevity, Occult & Deep Research, Unearned Wealth/Inheritance, Sudden Transformations & Secrets',
    meaningHi: 'दीर्घायु, गूढ़ विद्या व अनुसंधान, पैतृक संपत्ति, आकस्मिक परिवर्तन व रूपांतरण',
    vargas: ['D1', 'D8', 'D20']
  },
  9: {
    titleEn: '9th House (Dharma)',
    titleHi: 'नवम भाव (धर्म/भाग्य)',
    sanskritName: 'Dharma & Bhagya Bhava',
    meaningEn: 'Higher Wisdom, Guru/Mentors, Divine Fortune, Father, Pilgrimages, Law & Ethical Orientation',
    meaningHi: 'धर्म निष्ठा, गुरु कृपा, ईश्वरीय भाग्य, पिता, तीर्थाटन, उच्च विद्या व सदाचार',
    vargas: ['D1', 'D9', 'D12']
  },
  10: {
    titleEn: '10th House (Karma)',
    titleHi: 'दशम भाव (कर्म)',
    sanskritName: 'Karma & Rajya Bhava',
    meaningEn: 'Career, Vocation, Public Authority, Executive Leadership, Social Prestige & Societal Duty',
    meaningHi: 'आजीविका, पद प्रतिष्ठा, प्रशासनिक अधिकार, सामाजिक ख्याति व कर्म प्रभाव',
    vargas: ['D1', 'D9', 'D10']
  },
  11: {
    titleEn: '11th House (Labha)',
    titleHi: 'एकादश भाव (लाभ)',
    sanskritName: 'Labha & Aaya Bhava',
    meaningEn: 'Income Streams, Net Profits, Fulfillment of Aspirations, Social Networks & Elder Siblings',
    meaningHi: 'निरंतर आय, लाभ संवृद्धि, अभीष्ट सिद्धि, प्रभावशाली मित्र मंडल व ज्येष्ठ भ्राता',
    vargas: ['D1', 'D10', 'D11']
  },
  12: {
    titleEn: '12th House (Vyaya)',
    titleHi: 'द्वादश भाव (व्यय/मोक्ष)',
    sanskritName: 'Vyaya & Moksha Bhava',
    meaningEn: 'Spiritual Liberation (Moksha), Foreign Settlements, Seclusion, Subconscious Rest, Charity & Expenditure',
    meaningHi: 'मोक्ष व आत्मज्ञान, विदेश वास, एकांत साधना, शयन सुख, त्याग व दान-व्यय',
    vargas: ['D1', 'D9', 'D20']
  }
};

const BHAVESH_TITLE_NAMES: Record<number, { en: string; hi: string }> = {
  1: { en: 'Lagnesh (1L)', hi: 'लग्नेश (1L)' },
  2: { en: 'Dhanesh (2L)', hi: 'धनेश (2L)' },
  3: { en: 'Sahajesh (3L)', hi: 'सहजेश (3L)' },
  4: { en: 'Sukhesh (4L)', hi: 'सुखेश (4L)' },
  5: { en: 'Putresh (5L)', hi: 'पंचमेश (5L)' },
  6: { en: 'Shashthesh (6L)', hi: 'षष्ठेश (6L)' },
  7: { en: 'Saptamesh (7L)', hi: 'सप्तमेश (7L)' },
  8: { en: 'Ashtamesh (8L)', hi: 'अष्टमेश (8L)' },
  9: { en: 'Bhagyesh (9L)', hi: 'भाग्येश (9L)' },
  10: { en: 'Karmesh (10L)', hi: 'दशमेश (10L)' },
  11: { en: 'Labhesh (11L)', hi: 'लाभेश (11L)' },
  12: { en: 'Vyayesh (12L)', hi: 'द्वादशेश (12L)' }
};

/**
 * Computes the functional role of a planet based on the Ascendant sign.
 */
export function calculateFunctionalRole(
  planet: PlanetName,
  ascendantIndex: number,
  ownedHouses: number[]
): FunctionalRoleInfo {
  if (planet === 'Ascendant' || planet === 'Rahu' || planet === 'Ketu') {
    return {
      code: 'Chaya',
      titleEn: planet === 'Ascendant' ? 'Ascendant Point' : 'Chaya Graha (Lunar Node)',
      titleHi: planet === 'Ascendant' ? 'लग्न बिंदु' : 'छाया ग्रह (नोड)',
      isLagnesh: false,
      isYogakaraka: false,
      isMaraka: false,
      isTrikonaLord: false,
      isKendraLord: false,
      isDusthanaLord: false,
      isFunctionalBenefic: false,
      isFunctionalMalefic: false,
      isNeutral: true,
      ownedHouses: []
    };
  }

  const isLagnesh = ownedHouses.includes(1);
  const kendras = [1, 4, 7, 10];
  const trikonas = [1, 5, 9];
  const dusthanas = [6, 8, 12];
  const marakas = [2, 7];

  const hasKendra = ownedHouses.some(h => kendras.includes(h));
  const hasTrikona = ownedHouses.some(h => trikonas.includes(h));
  const isYogakaraka = (ownedHouses.includes(4) || ownedHouses.includes(7) || ownedHouses.includes(10)) &&
                       (ownedHouses.includes(5) || ownedHouses.includes(9));

  const isMaraka = ownedHouses.some(h => marakas.includes(h)) && !isLagnesh;
  const isTrikonaLord = hasTrikona;
  const isKendraLord = hasKendra;
  const isDusthanaLord = ownedHouses.some(h => dusthanas.includes(h)) && !isLagnesh && !isTrikonaLord;

  let code = ownedHouses.map(h => `${h}L`).join(' + ');
  if (!code) code = 'Neutral';

  // Construct descriptive title
  let titleEn = '';
  let titleHi = '';

  if (isLagnesh) {
    titleEn = 'Lagnesh (1L)';
    titleHi = 'लग्नेश (1L)';
    if (ownedHouses.length > 1) {
      const other = ownedHouses.find(h => h !== 1);
      if (other) {
        titleEn = `Lagnesh (1L) & ${BHAVESH_TITLE_NAMES[other]?.en || `${other}L`}`;
        titleHi = `लग्नेश (1L) व ${BHAVESH_TITLE_NAMES[other]?.hi || `${other}L`}`;
      }
    }
  } else if (isYogakaraka) {
    const namesEn = ownedHouses.map(h => BHAVESH_TITLE_NAMES[h]?.en || `${h}L`).join(' + ');
    const namesHi = ownedHouses.map(h => BHAVESH_TITLE_NAMES[h]?.hi || `${h}L`).join(' + ');
    titleEn = `${namesEn} / Supreme Yogakaraka`;
    titleHi = `${namesHi} (परम योगकारक)`;
  } else if (ownedHouses.length === 1) {
    titleEn = BHAVESH_TITLE_NAMES[ownedHouses[0]]?.en || `${ownedHouses[0]}L`;
    titleHi = BHAVESH_TITLE_NAMES[ownedHouses[0]]?.hi || `${ownedHouses[0]}L`;
  } else if (ownedHouses.length === 2) {
    titleEn = `${BHAVESH_TITLE_NAMES[ownedHouses[0]]?.en || `${ownedHouses[0]}L`} + ${BHAVESH_TITLE_NAMES[ownedHouses[1]]?.en || `${ownedHouses[1]}L`}`;
    titleHi = `${BHAVESH_TITLE_NAMES[ownedHouses[0]]?.hi || `${ownedHouses[0]}L`} व ${BHAVESH_TITLE_NAMES[ownedHouses[1]]?.hi || `${ownedHouses[1]}L`}`;
  }

  const isFunctionalBenefic = isLagnesh || isYogakaraka || (hasTrikona && !isDusthanaLord);
  const isFunctionalMalefic = isDusthanaLord || (ownedHouses.includes(3) && ownedHouses.includes(6)) || (ownedHouses.includes(11) && !isLagnesh && !hasTrikona);
  const isNeutral = !isFunctionalBenefic && !isFunctionalMalefic;

  return {
    code,
    titleEn: titleEn || code,
    titleHi: titleHi || code,
    isLagnesh,
    isYogakaraka,
    isMaraka,
    isTrikonaLord,
    isKendraLord,
    isDusthanaLord,
    isFunctionalBenefic,
    isFunctionalMalefic,
    isNeutral,
    ownedHouses
  };
}

/**
 * Calculates planetary aspect houses (graha drishti).
 */
export function calculatePlanetAspectHouses(
  planet: PlanetName,
  sourceHouse: number
): { houses: number[]; formattedEn: string; formattedHi: string } {
  if (planet === 'Ascendant') return { houses: [], formattedEn: 'None', formattedHi: 'कोई नहीं' };

  const aspects: number[] = [];

  // All 7 classical planets + Rahu/Ketu cast 7th aspect
  const addAspect = (offset: number) => {
    const target = ((sourceHouse - 1 + offset) % 12) + 1;
    if (!aspects.includes(target)) aspects.push(target);
  };

  // Universal 7th aspect (6 houses away)
  addAspect(6);

  // Mars: 4th (offset 3) and 8th (offset 7)
  if (planet === 'Mars') {
    addAspect(3);
    addAspect(7);
  }

  // Jupiter: 5th (offset 4) and 9th (offset 8)
  if (planet === 'Jupiter') {
    addAspect(4);
    addAspect(8);
  }

  // Saturn: 3rd (offset 2) and 10th (offset 9)
  if (planet === 'Saturn') {
    addAspect(2);
    addAspect(9);
  }

  // Rahu & Ketu: 5th (offset 4) and 9th (offset 8)
  if (planet === 'Rahu' || planet === 'Ketu') {
    addAspect(4);
    addAspect(8);
  }

  aspects.sort((a, b) => a - b);

  const formattedEn = aspects.map(h => `${h}H`).join(', ');
  const formattedHi = aspects.map(h => `${h} भाव`).join(', ');

  return { houses: aspects, formattedEn, formattedHi };
}

/**
 * Calculates D9 Navamsha sign for a given longitude.
 */
export function calculateD9Navamsha(longitude: number): { sign: ZodiacSign; signIndex: number; lord: PlanetName } {
  const norm = ((longitude % 360) + 360) % 360;
  const rashiIndex = Math.floor(norm / 30); // 0 to 11
  const degInSign = norm % 30;
  const navamshaPart = Math.floor(degInSign / (30 / 9)); // 0 to 8

  // Fire (0,4,8 -> Aries/0); Earth (1,5,9 -> Capricorn/9); Air (2,6,10 -> Libra/6); Water (3,7,11 -> Cancer/3)
  let startSignIndex = 0;
  if ([0, 4, 8].includes(rashiIndex)) startSignIndex = 0; // Aries
  else if ([1, 5, 9].includes(rashiIndex)) startSignIndex = 9; // Capricorn
  else if ([2, 6, 10].includes(rashiIndex)) startSignIndex = 6; // Libra
  else startSignIndex = 3; // Cancer

  const targetSignIndex = (startSignIndex + navamshaPart) % 12;
  const sign = ZODIAC_SIGNS[targetSignIndex];
  const lord = ZODIAC_LORDS[sign] as PlanetName;

  return { sign, signIndex: targetSignIndex, lord };
}

/**
 * Main Deterministic Engine:
 * Generates the unified, complete, and mathematically validated Planetary & Bhavesh Matrices.
 */
export function buildCompletePlanetaryBhaveshMatrix(
  kundali: CompleteKundali,
  lang: Language = 'en'
): CompletePlanetaryBhaveshMatrix {
  const isHi = lang === 'hi';
  const ascendantHouse = kundali.houses?.find(h => h.houseNumber === 1) || kundali.houses?.[0];
  const ascSign = ascendantHouse?.sign || 'Aries';
  const ascIndex = ascendantHouse ? ascendantHouse.signIndex : 0;
  const ascDegreeFormatted = kundali.planets?.find(p => p.name === 'Ascendant')?.degreeFormatted || '00° 00\' 00"';

  // Use precomputed Shadbala or calculate with fallback
  const shadbalaRes = kundali.shadbala || calculateShadbala(kundali.planets || [], kundali.houses || [], {
    birthDate: getBirthUtcDate(kundali.birthDetails),
    latitude: kundali.birthDetails.latitude,
    longitude: kundali.birthDetails.longitude
  });
  const prastaraTables = calculatePrastaraTables(kundali);

  const currentMD = kundali.dashas?.find(d => d.isCurrent);
  const currentAD = currentMD?.antardashas?.find(a => a.isCurrent);
  const currentPD = currentAD?.antardashas?.find(p => (p as any).isCurrent);

  // Map Houses owned by each planet dynamically from Kundali Houses
  const planetOwnedHousesMap: Record<string, number[]> = {};
  kundali.houses?.forEach(h => {
    const lord = h.signLord;
    if (lord) {
      if (!planetOwnedHousesMap[lord]) planetOwnedHousesMap[lord] = [];
      planetOwnedHousesMap[lord].push(h.houseNumber);
    }
  });

  const planetsOrder: PlanetName[] = [
    'Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'
  ];

  const planetaryEntries: PlanetaryMatrixEntry[] = planetsOrder.map(planetName => {
    const rawPlanet = kundali.planets?.find(p => p.name === planetName) || {
      name: planetName,
      symbol: planetName.slice(0, 2),
      longitude: 0,
      sign: 'Aries',
      signIndex: 0,
      signLord: 'Mars',
      degreeInSign: 0,
      degreeFormatted: '00°00\'00"',
      house: 1,
      nakshatra: 'Ashwini',
      nakshatraIndex: 0,
      nakshatraLord: 'Ketu',
      pada: 1,
      isRetrograde: false,
      isCombust: false,
      dignity: 'Neutral' as const
    };

    const ownedHouses = (planetOwnedHousesMap[planetName] || []).sort((a, b) => a - b);
    const functionalRole = calculateFunctionalRole(planetName, ascIndex, ownedHouses);

    // Natural Nature
    let naturalNature: PlanetaryMatrixEntry['naturalNature'] = 'Natural Benefic';
    let naturalNatureHi = 'नैसर्गिक शुभ';

    if (planetName === 'Sun' || planetName === 'Moon') {
      naturalNature = 'Luminary';
      naturalNatureHi = 'प्रकाश पुंज / प्रत्यक्ष देव';
    } else if (planetName === 'Rahu' || planetName === 'Ketu') {
      naturalNature = 'Chaya Graha';
      naturalNatureHi = 'छाया ग्रह';
    } else if (['Saturn', 'Mars'].includes(planetName)) {
      naturalNature = 'Natural Malefic';
      naturalNatureHi = 'नैसर्गिक क्रूर/पाप';
    } else {
      naturalNature = 'Natural Benefic';
      naturalNatureHi = 'नैसर्गिक सौम्य/शुभ';
    }

    // Lordship string
    let lordshipEn = 'Chaya Graha (No Lordship)';
    let lordshipHi = 'छाया ग्रह (भाव स्वामित्व नहीं)';

    if (ownedHouses.length === 1) {
      lordshipEn = `${ownedHouses[0]}L — ${ownedHouses[0]}${ownedHouses[0] === 1 ? 'st' : ownedHouses[0] === 2 ? 'nd' : ownedHouses[0] === 3 ? 'rd' : 'th'} House`;
      lordshipHi = `${ownedHouses[0]}L — ${ownedHouses[0]} भाव`;
    } else if (ownedHouses.length > 1) {
      const housesStr = ownedHouses.map(h => `${h}${h === 1 ? 'st' : h === 2 ? 'nd' : h === 3 ? 'rd' : 'th'}`).join(' & ');
      lordshipEn = `${ownedHouses.map(h => `${h}L`).join(' + ')} — ${housesStr} Houses`;
      lordshipHi = `${ownedHouses.map(h => `${h}L`).join(' + ')} — ${ownedHouses.join(' व ')} भाव`;
    }

    // Placement
    const occupiedHouse = rawPlanet.house || 1;
    const occupiedSign = rawPlanet.sign || 'Aries';
    const occupiedSignIndex = rawPlanet.signIndex ?? ZODIAC_SIGNS.indexOf(occupiedSign);
    const degreeInSign = rawPlanet.degreeInSign ?? 0;
    const formattedDegree = rawPlanet.degreeFormatted || `${degreeInSign.toFixed(2)}°`;

    const placementShortEn = `${occupiedHouse}H • ${occupiedSign} • ${degreeInSign.toFixed(1)}°`;
    const placementShortHi = `${occupiedHouse} भाव • ${occupiedSign} • ${degreeInSign.toFixed(1)}°`;

    // Lordship -> Placement (Deterministic Vector)
    let vectorEn = `Chaya in ${occupiedHouse}H`;
    let vectorHi = `छाया ग्रह ${occupiedHouse} भाव में`;

    const ownSignsForPlanet = PLANET_OWN_SIGNS[planetName] || [];
    const isOwnHousePlacement = ownSignsForPlanet.includes(occupiedSign);
    const isExaltedPlacement = rawPlanet.dignity === 'Exalted';
    const isDebilitatedPlacement = rawPlanet.dignity === 'Debilitated';

    if (ownedHouses.length > 0) {
      const lordsStr = ownedHouses.map(h => `${h}L`).join(' + ');
      vectorEn = `${lordsStr} → ${occupiedHouse}H`;
      vectorHi = `${lordsStr} ➔ ${occupiedHouse} भाव`;
    }

    let descriptionEn = `${planetName} is placed in House ${occupiedHouse} (${occupiedSign})`;
    let descriptionHi = `${planetName} ${occupiedHouse} भाव (${occupiedSign}) में स्थित है`;

    if (isOwnHousePlacement) {
      descriptionEn += ' (Own House / Swakshetra)';
      descriptionHi += ' (स्वग्रही)';
    } else if (isExaltedPlacement) {
      descriptionEn += ' (Exalted / Param Uccha)';
      descriptionHi += ' (परम उच्च)';
    } else if (isDebilitatedPlacement) {
      descriptionEn += ' (Debilitated / Neecha)';
      descriptionHi += ' (नीच राशि)';
    }

    // D9 Navamsha Calculation
    const d9Info = calculateD9Navamsha(rawPlanet.longitude);
    const isVargottama = d9Info.sign === occupiedSign;
    const isPushkarNav = isPushkarNavamsha(occupiedSignIndex, d9Info.signIndex);
    const isPushkarBhg = isPushkarBhaga(occupiedSignIndex, degreeInSign);

    const d9FormattedEn = `${occupiedSign} ${degreeInSign.toFixed(1)}° → ${d9Info.sign} Navamsha`;
    const d9FormattedHi = `${occupiedSign} ${degreeInSign.toFixed(1)}° ➔ ${d9Info.sign} नवमांश`;

    // Exact Dignity
    const exactDignityName = getExactDignity(planetName, occupiedSignIndex, degreeInSign);
    let dignityScore = 50;
    if (exactDignityName.includes('Exalted')) dignityScore = 100;
    else if (exactDignityName.includes('Moolatrikona')) dignityScore = 85;
    else if (exactDignityName.includes('Own')) dignityScore = 75;
    else if (exactDignityName.includes('Friend')) dignityScore = 65;
    else if (exactDignityName.includes('Neutral')) dignityScore = 50;
    else if (exactDignityName.includes('Enemy')) dignityScore = 35;
    else if (exactDignityName.includes('Debilitated')) dignityScore = 15;

    // Sign Lord & Planet -> Sign Lord Relationship
    const signLord = (ZODIAC_LORDS[occupiedSign] || 'Mars') as PlanetName;
    let signLordRelDirectionEn = `${planetName} → ${signLord}: Self`;
    let signLordRelDirectionHi = `${planetName} ➔ ${signLord}: स्वग्रही`;

    let naturalRel: 'Friend' | 'Neutral' | 'Enemy' | 'Self' = 'Self';
    let temporalRel: 'Friend' | 'Enemy' | 'Self' = 'Self';
    let compoundRel: 'Great Friend' | 'Friend' | 'Neutral' | 'Enemy' | 'Great Enemy' | 'Self' = 'Self';

    if (planetName !== signLord && planetName !== 'Ascendant' && planetName !== 'Rahu' && planetName !== 'Ketu') {
      const friendships = NATURAL_FRIENDSHIPS[planetName];
      if (friendships) {
        if (friendships.friends.includes(signLord)) naturalRel = 'Friend';
        else if (friendships.enemies.includes(signLord)) naturalRel = 'Enemy';
        else naturalRel = 'Neutral';
      }

      // Temporal Relationship based on distance from occupied house to signLord's house
      const signLordPos = kundali.planets?.find(p => p.name === signLord);
      const signLordHouse = signLordPos?.house || occupiedHouse;
      const houseDistance = ((occupiedHouse - signLordHouse + 12) % 12) + 1;
      const isTemporalFriend = [2, 3, 4, 10, 11, 12].includes(houseDistance);
      temporalRel = isTemporalFriend ? 'Friend' : 'Enemy';

      compoundRel = getPanchadhaMaitriCompound(naturalRel as any, temporalRel as any);
      const compoundLabelHi = getCompoundRelationshipHindi(compoundRel as any);

      signLordRelDirectionEn = `${planetName} → ${signLord}: ${naturalRel} (Compound: ${compoundRel})`;
      signLordRelDirectionHi = `${planetName} ➔ ${signLord}: ${naturalRel === 'Friend' ? 'मित्र' : naturalRel === 'Enemy' ? 'शत्रु' : 'सम'} (${compoundLabelHi})`;
    } else if (planetName === 'Rahu' || planetName === 'Ketu') {
      signLordRelDirectionEn = `${planetName} in ${signLord}'s domain`;
      signLordRelDirectionHi = `${planetName} ${signLord} के क्षेत्र में`;
      naturalRel = 'Neutral';
      temporalRel = 'Neutral' as any;
      compoundRel = 'Neutral';
    }

    // Nakshatra Info
    const nakshatraData = {
      name: rawPlanet.nakshatra || 'Ashwini',
      index: rawPlanet.nakshatraIndex ?? 0,
      pada: rawPlanet.pada || 1,
      lord: (rawPlanet.nakshatraLord || 'Ketu') as PlanetName,
      formatted: `${rawPlanet.nakshatra || 'Ashwini'} P${rawPlanet.pada || 1}`,
      deity: 'Vedic Divinity',
      deityHi: 'वैदिक देव'
    };

    // Condition Flags
    const flagsEn: string[] = [];
    const flagsHi: string[] = [];

    if (rawPlanet.isRetrograde) {
      flagsEn.push('Retrograde (Vakri)');
      flagsHi.push('वक्री (R)');
    } else {
      flagsEn.push('Direct (Margi)');
      flagsHi.push('मार्गी');
    }

    if (rawPlanet.isCombust) {
      flagsEn.push('Combust (Asta)');
      flagsHi.push('अस्त (Combust)');
    }

    if (isVargottama) {
      flagsEn.push('Vargottama');
      flagsHi.push('वर्गोत्तम');
    }
    if (isExaltedPlacement) {
      flagsEn.push('Exalted');
      flagsHi.push('उच्च');
    }
    if (isDebilitatedPlacement) {
      flagsEn.push('Debilitated');
      flagsHi.push('नीच');
    }
    if (isPushkarNav) {
      flagsEn.push('Pushkar Navamsha');
      flagsHi.push('पुष्कर नवमांश');
    }

    // Shadbala Item
    const planetShad = shadbalaRes?.planets?.find(s => s.planet === planetName);
    const shadbalaRatio = planetShad?.ratio ?? 1.0;
    const totalRupas = planetShad?.totalRupas ?? 6.0;

    // BAV Score for this planet in the occupied sign
    const bavScore = prastaraTables?.[planetName]?.signTotals?.[occupiedSignIndex] ?? 5;

    // Drishti
    const drishtiInfo = calculatePlanetAspectHouses(planetName, occupiedHouse);
    const aspectDetails: PlanetaryMatrixEntry['drishti']['aspectDetails'] = drishtiInfo.houses.map(targetH => {
      const targetHouseObj = kundali.houses?.find(h => h.houseNumber === targetH);
      const targetSign = targetHouseObj?.sign || 'Aries';
      const occupantsInTarget = kundali.planets?.filter(p => p.house === targetH && p.name !== 'Ascendant').map(p => p.name) || [];
      const dist = ((targetH - occupiedHouse + 12) % 12) + 1;
      let aspectType = '7th Direct Drishti';
      if (dist === 4) aspectType = '4th Chaturtha Drishti (Mars)';
      if (dist === 8) aspectType = '8th Ashta Drishti (Mars)';
      if (dist === 5) aspectType = '5th Trikona Drishti (Jupiter/Nodes)';
      if (dist === 9) aspectType = '9th Trikona Drishti (Jupiter/Nodes)';
      if (dist === 3) aspectType = '3rd Tritya Drishti (Saturn)';
      if (dist === 10) aspectType = '10th Dashama Drishti (Saturn)';

      return {
        targetHouse: targetH,
        targetSign,
        aspectType,
        virupas: 60,
        planetsAspected: occupantsInTarget
      };
    });

    // Dasha Status
    const isMahadashaActive = currentMD?.planet === planetName;
    const isAntardashaActive = currentAD?.planet === planetName;
    const isPratyantardashaActive = (currentPD as any)?.planet === planetName;

    let activeBadgeEn: string | undefined;
    let activeBadgeHi: string | undefined;

    if (isMahadashaActive) {
      activeBadgeEn = 'MD Active';
      activeBadgeHi = 'महादशा सक्रिय';
    } else if (isAntardashaActive) {
      activeBadgeEn = 'AD Active';
      activeBadgeHi = 'अंतर्दशा सक्रिय';
    } else if (isPratyantardashaActive) {
      activeBadgeEn = 'PD Active';
      activeBadgeHi = 'प्रत्यंतर्दशा सक्रिय';
    }

    return {
      planet: planetName,
      symbol: rawPlanet.symbol || planetName.slice(0, 2),
      naturalNature,
      naturalNatureHi,
      ownedHouses,
      functionalRole,
      lordship: {
        formattedEn: lordshipEn,
        formattedHi: lordshipHi,
        houses: ownedHouses
      },
      placement: {
        house: occupiedHouse,
        sign: occupiedSign,
        signIndex: occupiedSignIndex,
        degreeInSign,
        formattedDegree,
        formattedShortEn: placementShortEn,
        formattedShortHi: placementShortHi
      },
      lordshipPlacement: {
        vectorEn,
        vectorHi,
        isOwnHousePlacement,
        isExaltedPlacement,
        isDebilitatedPlacement,
        descriptionEn,
        descriptionHi
      },
      d1Rashi: occupiedSign,
      d9Navamsha: {
        sign: d9Info.sign,
        signIndex: d9Info.signIndex,
        lord: d9Info.lord,
        isVargottama,
        isPushkarNavamsha: isPushkarNav,
        isPushkarBhaga: isPushkarBhg,
        formattedEn: d9FormattedEn,
        formattedHi: d9FormattedHi
      },
      dignity: {
        level: exactDignityName,
        labelEn: exactDignityName,
        labelHi: isHi ? exactDignityName : exactDignityName,
        score: dignityScore,
        isExalted: isExaltedPlacement,
        isDebilitated: isDebilitatedPlacement,
        isOwnSign: isOwnHousePlacement,
        isMoolatrikona: exactDignityName.includes('Moolatrikona')
      },
      signLord,
      signLordRelationship: {
        directionEn: signLordRelDirectionEn,
        directionHi: signLordRelDirectionHi,
        natural: naturalRel,
        temporal: temporalRel,
        compound: compoundRel,
        compoundEn: compoundRel,
        compoundHi: compoundRel === 'Self' ? 'स्वयं' : (isHi ? getCompoundRelationshipHindi(compoundRel as any) : compoundRel)
      },
      nakshatra: nakshatraData,
      conditions: {
        isRetrograde: rawPlanet.isRetrograde || false,
        isCombust: rawPlanet.isCombust || false,
        isDirect: !rawPlanet.isRetrograde,
        isStationary: false,
        isVargottama,
        isExalted: isExaltedPlacement,
        isDebilitated: isDebilitatedPlacement,
        flagsEn,
        flagsHi
      },
      shadbala: {
        ratio: Number(shadbalaRatio.toFixed(2)),
        totalRupas: Number(totalRupas.toFixed(2)),
        category: shadbalaRatio >= 1.2 ? 'Exemplary' : shadbalaRatio >= 1.0 ? 'Fortified' : 'Deficient',
        rank: 1
      },
      bav: {
        score: bavScore,
        max: 8,
        formatted: `${bavScore}/8`
      },
      drishti: {
        housesAspected: drishtiInfo.houses,
        formattedEn: drishtiInfo.formattedEn,
        formattedHi: drishtiInfo.formattedHi,
        aspectDetails
      },
      dashaStatus: {
        isMahadashaActive,
        isAntardashaActive,
        isPratyantardashaActive,
        activeBadgeEn,
        activeBadgeHi
      }
    };
  });

  // Build the 12-House Bhavesh Matrix
  const bhaveshMatrix: BhaveshMatrixEntry[] = Array.from({ length: 12 }, (_, i) => {
    const hNum = i + 1;
    const houseObj = kundali.houses?.find(h => h.houseNumber === hNum) || {
      houseNumber: hNum,
      sign: ZODIAC_SIGNS[(ascIndex + i) % 12],
      signIndex: (ascIndex + i) % 12,
      signLord: ZODIAC_LORDS[ZODIAC_SIGNS[(ascIndex + i) % 12]],
      degreeStart: 0,
      planetsInHouse: []
    };

    const houseSign = houseObj.sign;
    const houseSignIndex = houseObj.signIndex;
    const bhaveshPlanetName = houseObj.signLord as PlanetName;
    const bhaveshPlanetaryEntry = planetaryEntries.find(p => p.planet === bhaveshPlanetName);
    const bhaveshRawPos = kundali.planets?.find(p => p.name === bhaveshPlanetName);

    const bhaveshPlacedHouse = bhaveshRawPos?.house || hNum;
    const bhaveshPlacedSign = bhaveshRawPos?.sign || houseSign;
    const bhaveshDegreeFormatted = bhaveshRawPos?.degreeFormatted || '00°00\'00"';

    const vectorEn = `${hNum}L → ${bhaveshPlacedHouse}H`;
    const vectorHi = `${hNum}L ➔ ${bhaveshPlacedHouse} भाव`;
    const isOwnHouse = bhaveshPlacedHouse === hNum;
    const distanceFromOwnHouse = ((bhaveshPlacedHouse - hNum + 12) % 12) + 1;

    const themeData = HOUSE_THEMES_DATA[hNum] || HOUSE_THEMES_DATA[1];

    // Occupants
    const occupants = kundali.planets?.filter(p => p.house === hNum && p.name !== 'Ascendant').map(p => {
      const isBenefic = ['Jupiter', 'Venus', 'Moon', 'Mercury'].includes(p.name);
      return {
        planet: p.name,
        dignity: p.dignity || 'Neutral',
        isBenefic,
        degreeFormatted: p.degreeFormatted || '00°00\'00"',
        isRetrograde: p.isRetrograde || false,
        isCombust: p.isCombust || false
      };
    }) || [];

    // Inbound Aspects received on this house
    const rawAspects = getAspectsOnHouse(hNum, kundali.planets || [], ascIndex);
    const aspectsReceived = rawAspects.map(a => ({
      planet: a.planet,
      fromHouse: a.sourceHouse,
      aspectType: a.aspectType,
      virupas: a.aspectType.includes('Special') ? 50 : 60,
      isBenefic: a.isBenefic
    }));

    // Outbound aspects given by Bhavesh
    const bhaveshAspects = calculatePlanetAspectHouses(bhaveshPlanetName, bhaveshPlacedHouse);
    const bhaveshAspectsGiven = bhaveshAspects.houses.map(tgt => ({
      targetHouse: tgt,
      aspectType: 'Graha Drishti',
      virupas: 60
    }));

    // SAV & BAV
    const savPoints = getSavBySign(kundali.ashtakvarga, houseSignIndex);
    const lordBavScore = getBavBySign(kundali.ashtakvarga, bhaveshPlanetName, houseSignIndex);

    let bavEvaluationEn = 'Steady / Neutral Capacity';
    let bavEvaluationHi = 'संतुलित क्षमता';
    if (savPoints >= 30) {
      bavEvaluationEn = 'Abundant Fortification (≥30 Bindus)';
      bavEvaluationHi = 'प्रचुर शुभ शक्ति (≥30 बिंदु)';
    } else if (savPoints < 25) {
      bavEvaluationEn = 'Sensitive / Vulnerable (<25 Bindus)';
      bavEvaluationHi = 'संवेदनशील क्षेत्र (<25 बिंदु)';
    }

    // Dasha Activation for Bhavesh
    const isMdLord = currentMD?.planet === bhaveshPlanetName;
    const isAdLord = currentAD?.planet === bhaveshPlanetName;
    const isPdLord = (currentPD as any)?.planet === bhaveshPlanetName;

    const isActive = isMdLord || isAdLord || isPdLord;
    let dashaRoleEn = 'Inactive';
    let dashaRoleHi = 'सुप्त';

    if (isMdLord) {
      dashaRoleEn = 'Current Mahadasha Lord (Primary Driver)';
      dashaRoleHi = 'वर्तमान महादशा स्वामी (मुख्य संचालक)';
    } else if (isAdLord) {
      dashaRoleEn = 'Current Antardasha Lord (Active Fructifier)';
      dashaRoleHi = 'वर्तमान अंतर्दशा स्वामी (सक्रिय फलदाता)';
    } else if (isPdLord) {
      dashaRoleEn = 'Current Pratyantardasha Lord';
      dashaRoleHi = 'वर्तमान प्रत्यंतर्दशा स्वामी';
    }

    return {
      houseNumber: hNum,
      houseTitleEn: themeData.titleEn,
      houseTitleHi: themeData.titleHi,
      sanskritName: themeData.sanskritName,
      bhavaMeaningEn: themeData.meaningEn,
      bhavaMeaningHi: themeData.meaningHi,
      sign: houseSign,
      signIndex: houseSignIndex,
      bhavesh: bhaveshPlanetName,
      bhaveshFunctionalRole: {
        code: `${hNum}L`,
        titleEn: BHAVESH_TITLE_NAMES[hNum]?.en || `${hNum}L`,
        titleHi: BHAVESH_TITLE_NAMES[hNum]?.hi || `${hNum}L`,
        isYogakaraka: bhaveshPlanetaryEntry?.functionalRole.isYogakaraka || false
      },
      bhaveshPlacement: {
        placedHouse: bhaveshPlacedHouse,
        placedSign: bhaveshPlacedSign,
        degreeFormatted: bhaveshDegreeFormatted,
        vectorEn,
        vectorHi,
        isOwnHouse,
        distanceFromOwnHouse
      },
      bhaveshDignity: {
        level: bhaveshPlanetaryEntry?.dignity.level || 'Neutral',
        labelEn: bhaveshPlanetaryEntry?.dignity.labelEn || 'Neutral',
        labelHi: bhaveshPlanetaryEntry?.dignity.labelHi || 'सम',
        isExalted: bhaveshPlanetaryEntry?.dignity.isExalted || false,
        isDebilitated: bhaveshPlanetaryEntry?.dignity.isDebilitated || false,
        isOwnSign: isOwnHouse
      },
      bhaveshCondition: {
        isCombust: bhaveshRawPos?.isCombust || false,
        isRetrograde: bhaveshRawPos?.isRetrograde || false,
        isDirect: !(bhaveshRawPos?.isRetrograde),
        statusSummaryEn: bhaveshPlanetaryEntry?.conditions.flagsEn.join(', ') || 'Direct',
        statusSummaryHi: bhaveshPlanetaryEntry?.conditions.flagsHi.join(', ') || 'मार्गी'
      },
      occupants,
      aspectsReceived,
      bhaveshAspectsGiven,
      bav: {
        houseSavPoints: savPoints,
        lordBavInSign: lordBavScore,
        evaluationEn: bavEvaluationEn,
        evaluationHi: bavEvaluationHi
      },
      relevantVargas: themeData.vargas,
      dashaActivation: {
        isActive,
        roleEn: dashaRoleEn,
        roleHi: dashaRoleHi
      }
    };
  });

  // Summary Metrics
  const lagneshEntry = bhaveshMatrix.find(b => b.houseNumber === 1);
  const dhaneshEntry = bhaveshMatrix.find(b => b.houseNumber === 2);
  const karmeshEntry = bhaveshMatrix.find(b => b.houseNumber === 10);
  const bhagyeshEntry = bhaveshMatrix.find(b => b.houseNumber === 9);
  const yogakarakaEntry = planetaryEntries.find(p => p.functionalRole.isYogakaraka);

  const exaltedPlanets = planetaryEntries.filter(p => p.dignity.isExalted).map(p => p.planet);
  const debilitatedPlanets = planetaryEntries.filter(p => p.dignity.isDebilitated).map(p => p.planet);
  const retrogradePlanets = planetaryEntries.filter(p => p.conditions.isRetrograde).map(p => p.planet);
  const combustPlanets = planetaryEntries.filter(p => p.conditions.isCombust).map(p => p.planet);

  return {
    ascendantSign: ascSign,
    ascendantDegreeFormatted: ascDegreeFormatted,
    planets: planetaryEntries,
    bhaveshMatrix,
    summary: {
      lagnesh: lagneshEntry?.bhavesh || 'Moon',
      lagneshPlacement: lagneshEntry?.bhaveshPlacement.vectorEn || '1L → 1H',
      yogakaraka: yogakarakaEntry?.planet,
      dhanesh: dhaneshEntry?.bhavesh || 'Sun',
      karmesh: karmeshEntry?.bhavesh || 'Mars',
      bhagyesh: bhagyeshEntry?.bhavesh || 'Jupiter',
      exaltedPlanets,
      debilitatedPlanets,
      retrogradePlanets,
      combustPlanets
    }
  };
}
